import PropTypes from 'prop-types';
import useTransaction from 'Hooks/useTransaction';
import { cn } from 'Utils/cn';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import useLocale from 'Hooks/useLocale';
import { formatCurrency, formatShortDate } from 'Utils/formatters';
import IconInfo from 'Components/Atoms/IconInfo/IconInfo';
import { REPLAY_BLOCK_CLASS } from 'Utils/constants';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineCloseSmall from 'Components/Atoms/OutlineIcons/OutlineCloseSmall';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import ReconciliationItemIcon from './ReconciliationItemIcon';

export default function ReconciliationInvoicesTransactionAsync(props) {
    const { transactionId, className, onDelete, showWarningAmount } = props;

    const { transaction, isLoading } = useTransaction(transactionId);
    const locale = useLocale();
    const { products } = useProvidersConnections();

    if (!transactionId) return null;

    if (isLoading) {
        return (
            <div className="h-10 flex items-center">
                <Skeleton className="w-full max-w-sm h-5" />
            </div>
        );
    }

    const hasProvider = !!transaction.provider_connection_product;

    const accountProduct = transaction.account_product_id
        ? products.find((product) => product.id === transaction.account_product_id)
        : null;
    const productName = accountProduct
        ? accountProduct.name
        : hasProvider
          ? transaction.provider_connection_product.name
          : '';

    if (!transaction.provider_connection_product) return null;

    const logoUrl = accountProduct
        ? accountProduct.provider_logo_url
        : transaction.provider_connection_product.provider_logo_url;
    const isIncome = transaction.amount * (transaction.reverse_sign ? -1 : 1) > 0;

    return (
        <div className={cn('relative group grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3', className)}>
            <ReconciliationItemIcon logoUrl={logoUrl} />

            <div className="min-w-0">
                <div className="truncate text-sm font-medium text-dark-blue">{transaction.description}</div>
                <div className="mt-0.5 truncate text-xs text-gray-500">{productName}</div>
            </div>

            <div className="text-right whitespace-nowrap pr-9">
                <div
                    className={cn(
                        'inline-block font-mono text-sm',
                        isIncome ? 'text-income' : 'text-dark-blue',
                        REPLAY_BLOCK_CLASS,
                        showWarningAmount && 'text-warning bg-warning/10 px-1 rounded',
                    )}
                >
                    {formatCurrency(transaction.amount, { currency: transaction.currency, locale })}
                </div>
                <div className="mt-0.5 flex items-center justify-end gap-1 text-xs text-gray-500">
                    {formatShortDate(transaction.date_booked, locale)}
                    {transaction.date_executed &&
                        transaction.date_executed.getUTCDate() !== transaction.date_booked.getUTCDate() && (
                            <IconInfo tooltip={transaction.date_executed.toLocaleDateString(locale)} />
                        )}
                </div>
            </div>

            {onDelete && (
                <div className="absolute top-1/2 -translate-y-1/2 right-0">
                    <IconButton
                        size="small"
                        icon={<OutlineCloseSmall />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                    />
                </div>
            )}
        </div>
    );
}

ReconciliationInvoicesTransactionAsync.propTypes = {
    transactionId: PropTypes.string.isRequired,
    onDelete: PropTypes.func,
    className: PropTypes.string,
    showWarningAmount: PropTypes.bool,
};
